import { Grid, Menu, MenuItem } from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { IRecordHeaders, IRecordHeaderWithChildren } from 'ui-component/records/types';
import { HierarchyBreadcrumb, HierarchyBreadcrumbLevel, HierarchyGridToolbar, LinkParentRecordDialog, OneRowOverlay } from './components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    filterChildRecordTypesByRoleActions,
    getCustomGroupingColDef,
    getHierarchyColumns,
    getTreeDataPath,
    hierarchyGridInitialState,
    transformQueryDataToGridData
} from './assets';
import { useDispatch } from 'store';
import { updateSectionName } from 'store/slices/section';
import { capitalize } from 'utils/stringHelpers';
import { useLazyQuery } from '@apollo/client';
import { QUERY_GET_RECORD_HEADERS_WITH_CHILDREN } from 'graphql/queries/bills';
import { openSnackbar } from 'store/slices/snackbar';
import { FindRecordType, FindRecordTypeVariables, IRecordType } from 'types/recordType';
import { QUERY_FIND_RECORD_TYPES } from 'graphql/queries/recordTypes';
// import { DataFocusViewDialog } from 'ui-component/dialogs';
import { SystemStyleObject } from '@mui/system';
import useMyMenuItems from 'hooks/useMyMenuItems';
import { CreateChildRecord } from 'ui-component/dialogs/DataFocusViewDialog/variants';
import { generateBreadcrumbLevels } from './utils';

export type HierarchyCoreProps = {
    recordId: string;
    initialRecord: IRecordHeaders;
    hideBreadcrumb?: boolean;
    containerSx?: SystemStyleObject;
};

export type RecordWithHierarchy = Pick<IRecordHeaders, 'id' | 'children' | 'recordType'> &
    Partial<IRecordHeaders> & {
        hierarchy: string[];
    };

export const HierarchyCore = ({ recordId, initialRecord, hideBreadcrumb, containerSx = {} }: HierarchyCoreProps) => {
    const isProject = !!initialRecord.isProject;
    const { actionsData } = useMyMenuItems();
    const apiRef = useGridApiRef();
    const dispatch = useDispatch();
    const recordTypeName = useMemo(() => initialRecord.recordType.name, [initialRecord.recordType.name]);
    const [breadcrumbLevels, setBreadcrumbLevels] = useState<HierarchyBreadcrumbLevel[]>([
        {
            id: initialRecord.id,
            name: `${capitalize(recordTypeName)}-${initialRecord.id}`,
            recordTypeId: +initialRecord.recordType.id,
            recordTypeName: initialRecord.recordType.name,
            level: initialRecord.level
        }
    ]);
    const [rows, setRows] = useState<RecordWithHierarchy[]>([]);
    const [recordToAddChild, setRecordToAddChild] = useState<RecordWithHierarchy | null>(null);
    const [recordToAddChildHierarchy, setRecordToAddChildHierarchy] = useState<string[]>([]);

    const [selectedRecordTypeToCreate, setSelectedRecordTypeToCreate] = useState<IRecordType | null>(null);
    const [childrenRecordTypeMenuAnchor, setChildrenRecordTypeMenuAnchor] = useState<HTMLElement | null>(null);
    const [selectedRecordChildren, setSelectedRecordChildren] = useState<IRecordType[]>([]);
    const [isOpenLinkParentRecord, setIsOpenLinkParentRecord] = useState(false);

    const [getRecordWithChildren, { data: recordDataWithChildrenQueryData, loading: loadingRecordWithChildren }] =
        useLazyQuery<IRecordHeaderWithChildren>(QUERY_GET_RECORD_HEADERS_WITH_CHILDREN, { fetchPolicy: 'no-cache' });

    const recordDataWithChildren = useMemo(
        () => recordDataWithChildrenQueryData?.getRecordHeaderWithChildren,
        [recordDataWithChildrenQueryData]
    );

    const [findRecordTypes] = useLazyQuery<FindRecordType, FindRecordTypeVariables>(QUERY_FIND_RECORD_TYPES);

    const snackBarDispatch = useCallback(
        (message: string, colorType: 'error' | 'success' = 'error') => {
            dispatch(
                openSnackbar({
                    open: true,
                    type: colorType,
                    title: capitalize(colorType),
                    subtitle: message
                })
            );
        },
        [dispatch]
    );

    const handleAddChildRecord =
        (recordTypeId: number = +initialRecord.recordType.id) =>
        async (currentTarget: HTMLButtonElement) => {
            try {
                const { data } = await findRecordTypes({ variables: { data: { id: recordTypeId, enabled: true } } });
                if (data?.findRecordType[0].children?.length) {
                    const filteredList: IRecordType[] = filterChildRecordTypesByRoleActions(
                        data.findRecordType[0].children,
                        actionsData || []
                    );

                    if (filteredList.length) {
                        setSelectedRecordChildren(filteredList);
                        setChildrenRecordTypeMenuAnchor(currentTarget);
                    } else {
                        snackBarDispatch(`This record does not have any children record types`);
                    }
                } else {
                    snackBarDispatch(`This record does not have any children record types`);
                }
            } catch (error: any) {
                console.log('error adding child record', error);
                snackBarDispatch(`Failed to show child records: ${error.message}`);
            }
        };

    const handleOpenChildForm = useCallback(
        (recordType: IRecordType, selectedRecord?: RecordWithHierarchy) => {
            setSelectedRecordTypeToCreate(recordType);

            const record: RecordWithHierarchy = selectedRecord || {
                ...initialRecord,
                hierarchy: [`${capitalize(recordTypeName)}-${initialRecord.id}`]
            };

            setRecordToAddChild(record);
            setRecordToAddChildHierarchy(record.hierarchy);
        },
        [initialRecord, recordTypeName]
    );

    const handleLinkParentRecord = useCallback(() => {
        setIsOpenLinkParentRecord(true);
    }, []);

    const handleCloseDataFocusViewDialog = (newRecord?: IRecordHeaders, errorMessage?: string) => {
        setSelectedRecordTypeToCreate(null);
        if (newRecord) {
            // setInitialRecordChildren((s) => [...s, newRecord]);
            const newRecordCreated: RecordWithHierarchy = {
                ...newRecord,
                hierarchy: [...recordToAddChildHierarchy, `${capitalize(newRecord.recordType.name)}-${newRecord.id}`]
            };
            const newRecordAlreadyExist = rows.find((r) => r.id === newRecordCreated.id);
            if (!newRecordAlreadyExist) setRows((p) => [...p, newRecordCreated]);
        }

        if (errorMessage) {
            snackBarDispatch(errorMessage);
        }
    };

    const handleCloseLinkParent = (wasParentLinked?: boolean) => {
        setIsOpenLinkParentRecord(false);
        if (wasParentLinked) {
            initData();
            snackBarDispatch(`Parent Linked`, 'success');
        }
    };

    const initData = useCallback(
        async (newRecordCreated?: RecordWithHierarchy) => {
            try {
                if (!recordDataWithChildren) return;
                const newRowsToAdd = transformQueryDataToGridData(recordDataWithChildren);
                setRows(newRowsToAdd);
                const levels = generateBreadcrumbLevels(recordDataWithChildren, initialRecord.id);
                setBreadcrumbLevels(levels);
            } catch (error: any) {
                console.log('error building the tree', error);
            }
        },
        [recordDataWithChildren, initialRecord.id]
    );

    const columns = useMemo(
        () => getHierarchyColumns(recordId, handleOpenChildForm, isProject ? undefined : handleLinkParentRecord),
        [recordId, handleOpenChildForm, handleLinkParentRecord, isProject]
    );

    // Dispatch the section name with the breadcrumb
    useEffect(() => {
        if (!hideBreadcrumb)
            dispatch(
                updateSectionName({
                    sectionName: <HierarchyBreadcrumb recordTypeName={capitalize(recordTypeName)} levels={breadcrumbLevels} />
                })
            );
    }, [breadcrumbLevels, dispatch, hideBreadcrumb, recordTypeName]);

    // Fetch the initial data
    useEffect(() => {
        if (!loadingRecordWithChildren) initData();
    }, [initData, loadingRecordWithChildren]);

    useEffect(() => {
        const autoResize = () => {
            requestAnimationFrame(() => {
                apiRef.current.autosizeColumns({
                    includeHeaders: true,
                    includeOutliers: true,
                    columns: [
                        ...apiRef.current
                            .getAllColumns()
                            .filter((col) => col.field !== '__tree_data_group__')
                            .map((col) => col.field)
                    ]
                });
            });
        };

        const unsubRowsSet = apiRef.current.subscribeEvent('rowsSet', autoResize);
        const unsubExpand = apiRef.current.subscribeEvent('rowExpansionChange', autoResize);
        const unsubFilter = apiRef.current.subscribeEvent('filterModelChange', autoResize);

        return () => {
            unsubExpand();
            unsubRowsSet();
            unsubFilter();
        };
    }, [apiRef]);

    useEffect(() => {
        if (initialRecord?.id) getRecordWithChildren({ variables: { recordHeaderId: Number(initialRecord.id) } });
    }, [getRecordWithChildren, initialRecord?.id]);

    return (
        <Grid item xs={12} sx={{ minHeight: '360px', position: 'relative', ...containerSx }}>
            <DataGridPro
                apiRef={apiRef}
                loading={loadingRecordWithChildren}
                initialState={hierarchyGridInitialState}
                columns={columns}
                rows={rows}
                density="compact"
                slots={{ toolbar: HierarchyGridToolbar }}
                getTreeDataPath={getTreeDataPath}
                groupingColDef={getCustomGroupingColDef(+initialRecord.id)}
                defaultGroupingExpansionDepth={-1}
                treeData
                sx={{
                    '& .MuiDataGrid-cell:focus': { outline: 'none' },
                    "& [data-id^='placeholder-children']": { display: 'none !important' }
                }}
                disableChildrenFiltering
                disableRowSelectionOnClick
                hideFooter
            />
            {!initialRecord.parent && !initialRecord.children?.length && (
                <OneRowOverlay
                    recordTypeId={Number(initialRecord?.recordType?.id)}
                    onAddChildRecord={handleAddChildRecord()}
                    onLinkParentRecord={isProject ? undefined : handleLinkParentRecord}
                />
            )}
            <ChildrenRecordTypeList
                anchorEl={childrenRecordTypeMenuAnchor}
                list={selectedRecordChildren}
                onClose={() => setChildrenRecordTypeMenuAnchor(null)}
                onOpenChildRecord={handleOpenChildForm}
            />
            {!!selectedRecordTypeToCreate && !!recordToAddChild && (
                <CreateChildRecord
                    open={!!selectedRecordTypeToCreate}
                    onClose={handleCloseDataFocusViewDialog}
                    recordType={selectedRecordTypeToCreate}
                    parent={recordToAddChild as IRecordHeaders}
                />
            )}
            {isOpenLinkParentRecord && (
                <LinkParentRecordDialog
                    open={isOpenLinkParentRecord}
                    onClose={handleCloseLinkParent}
                    recordType={initialRecord.recordType}
                    recordToAddParent={initialRecord}
                />
            )}
        </Grid>
    );
};

export type ChildrenRecordTypeListProps = {
    list: IRecordType[];
    anchorEl: HTMLElement | null;
    onClose: () => void;
    onOpenChildRecord: (recordType: IRecordType) => void;
};

export const ChildrenRecordTypeList = ({ list, anchorEl, onClose, onOpenChildRecord }: ChildrenRecordTypeListProps) => {
    const { loading } = useMyMenuItems();

    const handleOpenAddChildRecord = (selected: IRecordType) => {
        onOpenChildRecord(selected);
        onClose();
    };

    if (loading) return <></>;

    return (
        <Menu
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
                sx: ({ palette }) => ({
                    borderRadius: '8px',
                    marginTop: '20px',
                    backgroundColor: palette.primary[300],
                    maxHeight: '400px'
                })
            }}
        >
            {[...list]
                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                .map((child) => (
                    <MenuItem key={child.id} onClick={() => handleOpenAddChildRecord(child)} disabled={child.disabled as boolean}>
                        {child.name}
                    </MenuItem>
                ))}
        </Menu>
    );
};
